import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Sparkles, Loader2, AlertTriangle, Wand2, ShieldCheck, Receipt } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { contractsApi, invoicesApi, formatApiError } from "../../lib/ledgerApi";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";

const CURRENCIES = ["USD", "EUR", "GBP", "INR", "JPY", "AED", "NEO"];
const MODES = [
  { key: "form", label: "Form builder", icon: FileText },
  { key: "ai", label: "AI builder", icon: Sparkles },
];

export default function ContractNew() {
  const nav = useNavigate();
  const { business } = useAuth();
  const [mode, setMode] = useState("form");

  // Form state — escrow defaults
  const [type, setType] = useState("escrow");
  const [counterpartyVpa, setCounterpartyVpa] = useState("");
  const [arbiterVpa, setArbiterVpa] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [amount, setAmount] = useState("");
  // Escrow
  const [releaseCondition, setReleaseCondition] = useState("recipient_confirmation");
  const [timeoutDays, setTimeoutDays] = useState("7");
  const [releaseInstructions, setReleaseInstructions] = useState("");
  // Streaming
  const [ratePerSecond, setRatePerSecond] = useState("1");
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  // Conditional (single condition for v1 form simplicity)
  const [condType, setCondType] = useState("manual_flag");
  const [condName, setCondName] = useState("approved");
  const [condSetterVpa, setCondSetterVpa] = useState("");
  const [condOracleName, setCondOracleName] = useState("");
  const [condOracleValue, setCondOracleValue] = useState("");
  const [condWhen, setCondWhen] = useState("");
  const [logic, setLogic] = useState("all");
  const [onSatisfy, setOnSatisfy] = useState("release");
  const [onTimeout, setOnTimeout] = useState("refund");
  const [timeoutAt, setTimeoutAt] = useState("");
  // Invoice-linked
  const [eligibleInvoices, setEligibleInvoices] = useState([]);
  const [invoiceId, setInvoiceId] = useState("");
  const [fxLocking, setFxLocking] = useState("at_release");
  const [autoPayOn, setAutoPayOn] = useState("approval");

  const [busy, setBusy] = useState(false);

  // AI builder state
  const [description, setDescription] = useState("");
  const [building, setBuilding] = useState(false);
  const [draft, setDraft] = useState(null);

  // Load eligible invoices when type=invoice (where I'm the payer + status issued/overdue)
  useEffect(() => {
    if (type !== "invoice" || !business) return;
    invoicesApi.list({ role: "received" }).then((all) => {
      const open = (all || []).filter((i) => i.status === "issued" || i.status === "overdue");
      setEligibleInvoices(open);
    }).catch(() => {});
  }, [type, business]);

  // When an invoice is picked, auto-fill currency, amount, counterparty
  useEffect(() => {
    if (!invoiceId) return;
    const inv = eligibleInvoices.find((i) => i.id === invoiceId);
    if (!inv) return;
    setCurrency(inv.currency);
    setAmount(inv.amount);
    // Counterparty for an invoice contract = the issuer (we send TO them)
    // VPA isn't included on the invoice list response — recompute from legal_name slug
    // Fallback: just use the issuer's id surfaced through a future api; for now use legal_name
    if (inv.issuer_legal_name) {
      const slug = String(inv.issuer_legal_name).toLowerCase().split(/\s+/)[0];
      setCounterpartyVpa(`${slug}@neopay`);
    }
  }, [invoiceId, eligibleInvoices]);

  const buildSpec = () => {
    if (type === "escrow") {
      return {
        release_condition: releaseCondition,
        timeout_days: parseInt(timeoutDays, 10) || 7,
        arbiter_vpa: arbiterVpa || null,
        release_instructions: releaseInstructions || null,
      };
    }
    if (type === "streaming") {
      return {
        rate_per_second: ratePerSecond,
        start_at: new Date(startAt).toISOString(),
        end_at: new Date(endAt).toISOString(),
        recipient_vpa: counterpartyVpa,
      };
    }
    if (type === "conditional") {
      const cond = { type: condType };
      if (condType === "manual_flag") { cond.name = condName; cond.setter_vpa = condSetterVpa || null; }
      if (condType === "external_oracle") { cond.oracle_name = condOracleName; cond.expected_value = condOracleValue; }
      if (condType === "time_reached") { cond.when = new Date(condWhen).toISOString(); }
      return {
        conditions: [cond],
        logic,
        on_satisfy: onSatisfy,
        on_timeout: onTimeout,
        timeout_at: new Date(timeoutAt).toISOString(),
      };
    }
    if (type === "invoice") {
      return {
        invoice_id: invoiceId,
        auto_pay_on: autoPayOn,
        fx_locking: fxLocking,
      };
    }
    return {};
  };

  const onSubmit = async () => {
    if (!counterpartyVpa || !amount) {
      toast.error("Counterparty and amount required");
      return;
    }
    setBusy(true);
    try {
      const body = {
        contract_type: type,
        counterparty_vpa: counterpartyVpa,
        currency,
        total_amount: amount,
        spec: buildSpec(),
        arbiter_vpa: arbiterVpa || null,
      };
      const r = await contractsApi.draft(body);
      toast.success("Draft created", { description: "Review then sign & activate." });
      nav(`/app/contracts/${r.id}`);
    } catch (e) {
      toast.error("Create failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  const onAiBuild = async () => {
    if (!description.trim()) return;
    setBuilding(true);
    setDraft(null);
    try {
      const r = await contractsApi.build(description.trim(), null);
      setDraft(r);
      if (r.needs_clarification) {
        toast.warning("Clarification needed");
      }
    } catch (e) {
      toast.error("AI build failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBuilding(false);
    }
  };

  const onAiAccept = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const r = await contractsApi.draft({
        contract_type: draft.contract_type,
        counterparty_vpa: draft.counterparty_vpa,
        currency: draft.currency,
        total_amount: draft.total_amount,
        spec: draft.spec,
        arbiter_vpa: draft.arbiter_vpa || null,
      });
      toast.success("AI draft saved", { description: "Review then sign & activate." });
      nav(`/app/contracts/${r.id}`);
    } catch (e) {
      toast.error("Save draft failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl px-6 md:px-10 py-10 md:py-14" data-testid="contract-new-page">
      <button onClick={() => nav("/app/contracts")} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-6" data-testid="back-to-contracts">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to contracts
      </button>
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">/ Smart contracts · New</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-medium">Draft a new contract</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Two ways: structured form, or describe it in plain English and let the AI builder draft it for review.
        </p>
      </div>

      <div className="flex gap-2 mb-6">
        {MODES.map((m) => {
          const Icon = m.icon;
          const active = mode === m.key;
          return (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              data-testid={`mode-${m.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest border rounded-sm transition-colors ${active ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground"}`}
            >
              <Icon className="w-3 h-3" strokeWidth={1.5} /> {m.label}
            </button>
          );
        })}
      </div>

      {mode === "form" ? (
        <div className="border border-border rounded-sm bg-card p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Contract type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="type-select" className="rounded-sm font-mono mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="escrow">Escrow</SelectItem>
                  <SelectItem value="streaming">Streaming</SelectItem>
                  <SelectItem value="conditional">Conditional</SelectItem>
                  <SelectItem value="invoice">Invoice-linked</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="currency-select" className="rounded-sm font-mono mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Counterparty VPA</Label>
              <Input data-testid="counterparty-input" value={counterpartyVpa} onChange={(e) => setCounterpartyVpa(e.target.value)} placeholder="globex@neopay" className="rounded-sm font-mono mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Total amount</Label>
              <Input data-testid="amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="500.00" className="rounded-sm font-mono mt-1" />
            </div>
          </div>

          {type === "escrow" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Release condition</Label>
                  <Select value={releaseCondition} onValueChange={setReleaseCondition}>
                    <SelectTrigger data-testid="release-cond-select" className="rounded-sm font-mono mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recipient_confirmation">Recipient confirmation</SelectItem>
                      <SelectItem value="time_elapsed">Time elapsed</SelectItem>
                      <SelectItem value="arbiter_approval">Arbiter approval</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Timeout (days)</Label>
                  <Input data-testid="timeout-input" type="number" min="1" value={timeoutDays} onChange={(e) => setTimeoutDays(e.target.value)} className="rounded-sm font-mono mt-1" />
                </div>
              </div>
              {releaseCondition === "arbiter_approval" && (
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Arbiter VPA</Label>
                  <Input data-testid="arbiter-input" value={arbiterVpa} onChange={(e) => setArbiterVpa(e.target.value)} placeholder="initech@neopay" className="rounded-sm font-mono mt-1" />
                </div>
              )}
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Release instructions (optional)</Label>
                <Textarea data-testid="instructions-input" value={releaseInstructions} onChange={(e) => setReleaseInstructions(e.target.value)} rows={2} className="rounded-sm mt-1" />
              </div>
            </>
          )}

          {type === "streaming" && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Rate / second</Label>
                  <Input data-testid="rate-input" value={ratePerSecond} onChange={(e) => setRatePerSecond(e.target.value)} className="rounded-sm font-mono mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Start</Label>
                  <Input data-testid="start-input" type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} className="rounded-sm font-mono mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">End</Label>
                  <Input data-testid="end-input" type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)} className="rounded-sm font-mono mt-1" />
                </div>
              </div>
              <div className="text-[11px] text-muted-foreground font-mono">
                Total amount must equal rate × duration in seconds.
              </div>
            </>
          )}

          {type === "conditional" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Condition type</Label>
                  <Select value={condType} onValueChange={setCondType}>
                    <SelectTrigger data-testid="cond-type-select" className="rounded-sm font-mono mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual_flag">Manual flag</SelectItem>
                      <SelectItem value="external_oracle">External oracle (mock)</SelectItem>
                      <SelectItem value="time_reached">Time reached</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Logic</Label>
                  <Select value={logic} onValueChange={setLogic}>
                    <SelectTrigger className="rounded-sm font-mono mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All conditions</SelectItem>
                      <SelectItem value="any">Any condition</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {condType === "manual_flag" && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Flag name</Label>
                    <Input data-testid="flag-name-input" value={condName} onChange={(e) => setCondName(e.target.value)} className="rounded-sm font-mono mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Setter VPA (optional)</Label>
                    <Input data-testid="flag-setter-input" value={condSetterVpa} onChange={(e) => setCondSetterVpa(e.target.value)} placeholder="globex@neopay" className="rounded-sm font-mono mt-1" />
                  </div>
                </div>
              )}
              {condType === "external_oracle" && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Oracle name</Label>
                    <Input data-testid="oracle-name-input" value={condOracleName} onChange={(e) => setCondOracleName(e.target.value)} placeholder="shipment_status" className="rounded-sm font-mono mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Expected value</Label>
                    <Input data-testid="oracle-value-input" value={condOracleValue} onChange={(e) => setCondOracleValue(e.target.value)} placeholder="delivered" className="rounded-sm font-mono mt-1" />
                  </div>
                </div>
              )}
              {condType === "time_reached" && (
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">When</Label>
                  <Input data-testid="cond-when-input" type="datetime-local" value={condWhen} onChange={(e) => setCondWhen(e.target.value)} className="rounded-sm font-mono mt-1" />
                </div>
              )}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">On satisfy</Label>
                  <Select value={onSatisfy} onValueChange={setOnSatisfy}>
                    <SelectTrigger className="rounded-sm font-mono mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="release">Release</SelectItem>
                      <SelectItem value="refund">Refund</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">On timeout</Label>
                  <Select value={onTimeout} onValueChange={setOnTimeout}>
                    <SelectTrigger className="rounded-sm font-mono mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="release">Release</SelectItem>
                      <SelectItem value="refund">Refund</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Timeout at</Label>
                  <Input data-testid="cond-timeout-input" type="datetime-local" value={timeoutAt} onChange={(e) => setTimeoutAt(e.target.value)} className="rounded-sm font-mono mt-1" />
                </div>
              </div>
            </>
          )}

          {type === "invoice" && (
            eligibleInvoices.length === 0 ? (
              <div data-testid="invoice-empty-state" className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-4 text-sm flex items-start gap-3">
                <Receipt className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-400" strokeWidth={1.5} />
                <div>
                  <div className="font-medium">No eligible invoices</div>
                  <div className="text-muted-foreground text-xs mt-1">
                    You can only link a contract to an invoice issued <em>to</em> you and still in <code className="font-mono">issued</code> or <code className="font-mono">overdue</code> state.{" "}
                    <Link to="/app/invoices" className="underline" data-testid="invoice-empty-link">Open invoices →</Link>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Invoice to fund</Label>
                  <Select value={invoiceId} onValueChange={setInvoiceId}>
                    <SelectTrigger data-testid="invoice-select" className="rounded-sm font-mono mt-1">
                      <SelectValue placeholder="Pick an invoice…" />
                    </SelectTrigger>
                    <SelectContent>
                      {eligibleInvoices.map((i) => (
                        <SelectItem key={i.id} value={i.id} data-testid={`invoice-opt-${i.id}`}>
                          {i.amount} {i.currency} — from {i.issuer_legal_name || "?"} ({i.id.slice(0, 8)}…)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-[11px] text-muted-foreground mt-1 font-mono">
                    Picking one auto-fills currency / amount / counterparty.
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">FX locking</Label>
                    <Select value={fxLocking} onValueChange={setFxLocking}>
                      <SelectTrigger data-testid="fx-locking-select" className="rounded-sm font-mono mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="at_release">At release (live rate)</SelectItem>
                        <SelectItem value="at_activation">At activation (locked-in rate)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Auto-pay on</Label>
                    <Select value={autoPayOn} onValueChange={setAutoPayOn}>
                      <SelectTrigger data-testid="auto-pay-select" className="rounded-sm font-mono mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="approval">Approval (default)</SelectItem>
                        <SelectItem value="issuance">Issuance (immediate)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )
          )}

          <div className="flex justify-end pt-4 border-t border-border">
            <Button onClick={onSubmit} disabled={busy || (type === "invoice" && !invoiceId)} data-testid="submit-draft" className="rounded-sm">
              <FileText className="w-3 h-3 mr-1" strokeWidth={1.5} /> Save draft
            </Button>
          </div>
        </div>
      ) : (
        <div className="border border-border rounded-sm bg-card p-6 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Describe the contract</Label>
            <Textarea
              data-testid="ai-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="e.g. Lock 200 USD for globex@neopay, release when they confirm delivery, 14-day timeout."
              className="rounded-sm mt-1 font-mono"
            />
          </div>
          <Button onClick={onAiBuild} disabled={building || !description.trim()} data-testid="ai-build-btn" className="rounded-sm">
            {building ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" strokeWidth={1.5} />}
            Build draft
          </Button>

          {draft && (
            <div className="space-y-4 pt-4 border-t border-border" data-testid="ai-draft">
              {draft.ai_offline && (
                <div className="text-[11px] text-muted-foreground italic">AI offline — heuristic parser used.</div>
              )}
              {draft.needs_clarification ? (
                <div className="space-y-2 text-xs">
                  <div className="text-amber-600 dark:text-amber-400 inline-flex items-center gap-1.5">
                    <AlertTriangle className="w-3 h-3" strokeWidth={1.5} /> Clarifications needed
                  </div>
                  <ul className="font-mono text-muted-foreground space-y-1">
                    {draft.clarification_questions?.map((q, i) => <li key={i}>· {q}</li>)}
                  </ul>
                </div>
              ) : (
                <>
                  <pre className="text-[11px] font-mono text-muted-foreground p-3 bg-muted rounded-sm overflow-x-auto whitespace-pre-wrap">
{JSON.stringify({
  contract_type: draft.contract_type,
  counterparty_vpa: draft.counterparty_vpa,
  currency: draft.currency,
  total_amount: draft.total_amount,
  spec: draft.spec,
}, null, 2)}
                  </pre>
                  {draft.risk_analysis && (
                    <div className={`p-3 border rounded-sm text-xs space-y-1 ${
                      draft.risk_analysis.risk_level === "high" ? "border-status-tampered/40 bg-status-tampered/5"
                      : draft.risk_analysis.risk_level === "medium" ? "border-amber-500/40 bg-amber-500/5"
                      : "border-status-ok/40 bg-status-ok/5"
                    }`} data-testid="ai-risk-analysis">
                      <div className="flex items-center gap-1.5 font-medium">
                        <ShieldCheck className="w-3 h-3" strokeWidth={1.5} />
                        Risk: <span className="uppercase tracking-widest">{draft.risk_analysis.risk_level}</span>
                      </div>
                      {draft.risk_analysis.concerns?.length > 0 && (
                        <ul className="font-mono space-y-0.5 mt-1">
                          {draft.risk_analysis.concerns.map((c, i) => <li key={i}>· {c}</li>)}
                        </ul>
                      )}
                      {draft.risk_analysis.suggestions?.length > 0 && (
                        <div className="mt-2">
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Suggestions</div>
                          <ul className="font-mono space-y-0.5">
                            {draft.risk_analysis.suggestions.map((s, i) => <li key={i}>· {s}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  <Button onClick={onAiAccept} disabled={busy} data-testid="ai-accept" className="rounded-sm">
                    Save as draft
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
